import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Plus, ChevronDown, ChevronUp, RefreshCw, TrendingUp } from 'lucide-react';
import { clsx } from 'clsx';
import type { SwimLaneData } from './SwimLane';
import { HEADER_TRANSITION } from './animation';
import { CausalNarrativeDisplay } from './CausalNarrativeDisplay';

const CURRENCY_COMPACT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

interface StrategyClusterHeaderProps {
  lane: SwimLaneData;
  expanded: boolean;
  onToggle: () => void;
  onAdd?: () => void;
  onRequestRecompute?: () => void;
}

// ---------------------------------------------------------------------------
// Confidence badge
// ---------------------------------------------------------------------------

function ConfidenceBadge({
  label,
}: {
  label: 'established' | 'emerging' | 'noise' | undefined;
}) {
  if (!label) return null;
  const styles =
    label === 'established'
      ? 'bg-teal-50 text-teal-700 border-teal-200'
      : label === 'emerging'
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-slate-50 text-slate-500 border-slate-200';
  const text =
    label === 'established' ? 'Established' : label === 'emerging' ? 'Emerging' : 'Explore';
  return (
    <span
      className={clsx(
        'text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border',
        styles,
      )}
    >
      {text}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Concept pill
// ---------------------------------------------------------------------------

function ConceptPill({
  name,
  weight,
  edgeColor,
}: {
  name: string;
  weight: number;
  edgeColor: 'teal' | 'amber' | 'rose';
}) {
  const cls =
    edgeColor === 'teal'
      ? 'bg-teal-50 text-teal-700'
      : edgeColor === 'amber'
        ? 'bg-amber-50 text-amber-700'
        : 'bg-rose-50 text-rose-700';
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full',
        cls,
      )}
    >
      {name}
      <span className="text-[9px] opacity-60">{(weight * 100).toFixed(0)}%</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Centroid subtitle
// ---------------------------------------------------------------------------

function FormulaSubtitle({ lane }: { lane: SwimLaneData }) {
  const c = lane.cluster_metadata?.formula_centroid;
  if (!c) return null;
  const parts: string[] = [];
  if (c.hook_type) parts.push(`Hook: ${prettify(c.hook_type)}`);
  if (c.tone) parts.push(`Tone: ${prettify(c.tone)}`);
  if (c.visual_style) parts.push(`Visual: ${prettify(c.visual_style)}`);
  if (c.cta_strategy) parts.push(`CTA: ${prettify(c.cta_strategy)}`);
  if (parts.length === 0) return null;
  return <div className="text-[11px] text-slate-500 truncate">{parts.join(' · ')}</div>;
}

function prettify(slug: string): string {
  return slug
    .replace(/[-_]/g, ' ')
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ---------------------------------------------------------------------------
// CausalPillsView — raw PDO concept pills (used as fallback + technical view)
// ---------------------------------------------------------------------------

function CausalPillsView({
  meta,
}: {
  meta: NonNullable<import('./SwimLane').SwimLaneData['cluster_metadata']>;
}) {
  return (
    <div className="space-y-2">
      {meta.causal_signature.resolves_concern.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[9px] uppercase tracking-wide font-bold text-slate-500 mr-1">
            Resolves
          </span>
          {meta.causal_signature.resolves_concern.slice(0, 4).map((c) => (
            <ConceptPill key={c.concept_name} name={c.concept_name} weight={c.weight} edgeColor="teal" />
          ))}
        </div>
      )}
      {meta.causal_signature.drives.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[9px] uppercase tracking-wide font-bold text-slate-500 mr-1">
            Drives
          </span>
          {meta.causal_signature.drives.slice(0, 4).map((c) => (
            <ConceptPill key={c.concept_name} name={c.concept_name} weight={c.weight} edgeColor="amber" />
          ))}
        </div>
      )}
      {meta.causal_signature.inhibits.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[9px] uppercase tracking-wide font-bold text-slate-500 mr-1">
            Inhibits
          </span>
          {meta.causal_signature.inhibits.slice(0, 3).map((c) => (
            <ConceptPill key={c.concept_name} name={c.concept_name} weight={c.weight} edgeColor="rose" />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StrategyClusterHeader
// ---------------------------------------------------------------------------

export function StrategyClusterHeader({
  lane,
  expanded,
  onToggle,
  onAdd,
  onRequestRecompute,
}: StrategyClusterHeaderProps) {
  const [showCausal, setShowCausal] = useState(false);
  const [showTechnical, setShowTechnical] = useState(false);
  const meta = lane.cluster_metadata;
  const isEstablished = meta?.confidence_label === 'established';
  const hasNarrative = !!meta?.causal_narrative?.prose;

  return (
    <div
      className={clsx(
        'px-4 py-3 select-none',
        isEstablished &&
          'bg-gradient-to-r from-teal-50/40 via-white to-white border-l-2 border-l-teal-400',
      )}
    >
      <div className="flex items-center gap-3 cursor-pointer" onClick={onToggle}>
        <Sparkles
          size={15}
          className={clsx(
            'flex-shrink-0',
            isEstablished ? 'text-teal-500' : 'text-slate-400',
          )}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-sm text-navy-900 truncate">{lane.name}</span>
            <ConfidenceBadge label={meta?.confidence_label} />
            {meta?.is_seed && (
              <span className="text-[9px] uppercase tracking-wide font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                Seed
              </span>
            )}
          </div>
          <FormulaSubtitle lane={lane} />
        </div>

        <div className="flex items-center gap-3 text-xs text-slate-500 whitespace-nowrap">
          {lane.rollup.brief_count > 0 && (
            <span>
              {lane.rollup.brief_count} brief{lane.rollup.brief_count !== 1 ? 's' : ''}
            </span>
          )}
          {lane.rollup.spend > 0 && <span>${(lane.rollup.spend / 1000).toFixed(1)}k</span>}
          {lane.rollup.weighted_roas > 0 && (
            <span className="font-semibold text-navy-900">
              ROAS {lane.rollup.weighted_roas.toFixed(1)}x
            </span>
          )}
          {meta?.causal_lift_sigma !== undefined &&
            meta?.causal_lift_sigma !== null && (
              <span className="text-teal-600">
                +{meta.causal_lift_sigma.toFixed(1)}σ
              </span>
            )}
          {typeof lane.rollup.incremental_revenue === 'number' &&
            lane.rollup.incremental_revenue > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-2 py-0.5">
                <TrendingUp size={9} />
                {CURRENCY_COMPACT.format(lane.rollup.incremental_revenue)} incremental
              </span>
            )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {onRequestRecompute && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRequestRecompute();
              }}
              className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition"
              title="Re-run analysis"
            >
              <RefreshCw size={13} />
            </button>
          )}
          {onAdd && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAdd();
              }}
              className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition"
              title={`Generate more in ${lane.name}`}
            >
              <Plus size={14} />
            </button>
          )}
          {meta && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowCausal((v) => !v);
              }}
              className="text-[10px] text-slate-400 hover:text-teal-600 transition"
              title={showCausal ? 'Collapse causal signature' : 'Show causal signature'}
            >
              {showCausal ? 'hide' : 'why'}
            </button>
          )}
          {expanded ? (
            <ChevronUp size={14} className="text-slate-400" />
          ) : (
            <ChevronDown size={14} className="text-slate-400" />
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {showCausal && meta && (
          <motion.div
            key="causal-panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={HEADER_TRANSITION}
            className="overflow-hidden"
          >
            <div className="mt-3 px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-100 space-y-3">
              {/* Natural language narrative */}
              {hasNarrative ? (
                <CausalNarrativeDisplay narrative={meta.causal_narrative!} />
              ) : (
                /* Fallback: raw concept pills when no narrative exists */
                <CausalPillsView meta={meta} />
              )}

              {/* Persona alignment */}
              {Object.keys(meta.causal_signature.persona_alignment).length > 0 && (
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold mr-1">Persona alignment:</span>
                  {Object.entries(meta.causal_signature.persona_alignment)
                    .slice(0, 3)
                    .map(([personaName, score], i) => (
                      <span key={personaName}>
                        {i > 0 && <span className="text-slate-300 mx-1">·</span>}
                        {personaName}{' '}
                        <span className="font-semibold text-navy-900">
                          {score.toFixed(2)}
                        </span>
                      </span>
                    ))}
                </div>
              )}

              {/* Collapsible technical view (only shown when narrative exists) */}
              {hasNarrative && (
                <div>
                  <button
                    type="button"
                    className="text-[9px] uppercase tracking-wide text-slate-400 hover:text-slate-600 font-semibold transition-colors"
                    onClick={() => setShowTechnical((v) => !v)}
                  >
                    {showTechnical ? '− Hide technical view' : '+ Technical view'}
                  </button>
                  <AnimatePresence initial={false}>
                    {showTechnical && (
                      <motion.div
                        key="tech-view"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={HEADER_TRANSITION}
                        className="overflow-hidden"
                      >
                        <div className="mt-2 space-y-2">
                          <CausalPillsView meta={meta} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
